import {ContextPanelReactProps} from 'common/widgets/context-panel';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';
import {getProjectId, getReportUrl} from 'common/report/util';
import {ContextPanelData, DetailType} from 'common/widgets/context-panel/types';
import {ModelStatus} from 'common/util/model-resource/model-resource';
import {getEditCapability, getShareCapability} from 'common/report/util/saved-entities';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {Translatable} from 'common/i18n';

import type {ContextPanelRendererParams} from '../child-menu';

/**
 * Provides handlers for events inside a metric context panel
 */
export const createContextPanelEventHandlers = (
  params: Partial<ContextPanelRendererParams> = {},
): Partial<ContextPanelReactProps> => {
  // handles actions from, the context panel
  const handleContextPanelAction = async (ev: CustomEvent) => {
    const action = ev.detail.name;
    const behaviorId = ev.detail.item.id;

    if (action === `shareBehavior`) {
      await promptSharing({
        type: SharableEntityType.Behavior,
        id: behaviorId,
        'project-id': Number(getProjectId()),
      });
    } else if (ev.detail.item.url && action === `viewEntity`) {
      window.open(ev.detail.item.url, `_blank`);
    } else {
      throw new Error(`action handler not implemented`);
    }
  };

  const handleContextPanelEditDescription = async ({detail}: CustomEvent) => {
    const behaviorId = detail.item.id;
    const updatedDescription = detail.description;
    const behaviorsCtx = params.availableContexts.behaviors;

    try {
      await behaviorsCtx.patch(behaviorId, {description: updatedDescription});
    } catch {
      addToast({
        message: `Sorry, something went wrong. Please contact support if this problem persists.`,
        type: ToastType.ERROR,
        timeout: null,
      });
    }
  };

  return {
    onHoverIn: params.handleHoverIn,
    onHoverOut: params.handleHoverOut,
    onTooltipContextAction: handleContextPanelAction,
    onTooltipContextEditDescription: handleContextPanelEditDescription,
  };
};

/**
 * Generates the context panel data for Metrics to allow for sharing and description editing
 */
export function generateBehaviorContextPanelData({
  id,
  behaviorsCtx,
  canWriteBehaviors = false,
  canShare = false,
}: {
  id: number;
  behaviorsCtx: BehaviorsResource;
  canWriteBehaviors?: boolean;
  canShare?: boolean;
}): ContextPanelData {
  const model = behaviorsCtx.getOne(id);
  const behavior = model.value;

  if (model.status === ModelStatus.Loading || !behavior) {
    return null;
  }

  const permissions = getEditCapability(behavior);
  const actions: ContextPanelData['actions'] = [];
  const links: ContextPanelData['links'] = [];
  const details: ContextPanelData['details'] = [];

  // manage sharing and permissions action
  if (canShare) {
    actions.push({
      name: `shareBehavior`,
      label: (t) => t(`query-builder.context-menu.share`),
      icon: `invite`,
      // Only superadmin or editor can share
      disabled: !getShareCapability(behavior, EntityType.Behavior).enableShare,
      disabledTooltipText: (t) => t(`query-builder.context-menu.share-no-access`),
      item: {
        id: behavior.id,
        superadminOverrideShare: getShareCapability(behavior).requireSuperadminOverride,
      },
    });
  }

  // view entity in lexicon link
  links.push({
    icon: `share`,
    href: getReportUrl(`lexicon`, {projectId: getProjectId()}) + `#transformations/behaviors/${behavior.id}`,
    tooltipText: (t) => t(`query-builder.context-menu.view-in-lexicon`),
  });

  actions.push({
    name: `viewEntity`,
    label: (t) => t(`query-builder.context-menu.view-in-lexicon`),
    icon: `share`,
    item: {
      id: behavior.id,
      name: behavior.name,
      url: getReportUrl(`lexicon`, {projectId: getProjectId()}) + `#transformations/behaviors/${behavior.id}`,
    },
  });

  if (behavior.created_by) {
    details.push({
      name: (t) => t(`query-builder.context-menu.creator`),
      type: DetailType.User,
      value: {...behavior.created_by},
    });
  }

  const headers: Record<string, Translatable> = {
    funnel: (t) => t(`query-builder.metrics.funnel`),
    retention: (t) => t(`query-builder.metrics.retention`),
  };

  const icon = {
    funnel: `brand-funnels`,
    retention: `brand-retention`,
  }[behavior.type];

  return {
    actions,
    links,
    header: behavior.name,
    subHeader: headers[behavior.type],
    entityIcon: icon,
    description: behavior.description,
    entityType: EntityType.Behavior,
    excludedMetricIcons: [`pie`, `pulse`],
    details,
    // allows for editing the description of the metric
    editItem:
      canWriteBehaviors && permissions.enableEdit
        ? {
            id: behavior.id,
            type: EntityType.Behavior,
            name: behavior.name,
          }
        : null,
  };
}
